import { IonSpinner } from '@ionic/react';
import { useMutation } from '@apollo/client/react';
import { UPLOAD_CHANNEL_IMAGE, DELETE_CHANNEL_IMAGE } from '../../../../graphql/mutations/channelImage';
import { EditableImage } from '../../../components/photo-picker/EditableImage';
import { ChannelImage } from './ChannelImage';
import type {
  UploadChannelImageMutation,
  UploadChannelImageMutationVariables,
  DeleteChannelImageMutation,
  DeleteChannelImageMutationVariables,
} from '../../../../types/graphql-generated';

interface ChannelImageEditorProps {
  channelId: string;
  image?: string | null;
  updatedAt?: string | null;
  name: string;
  size: number;
}

/**
 * Channel image with a camera badge for its owner. The mutations return the
 * channel's `image` and `updatedAt`, so Apollo refreshes the cached channel.
 */
export const ChannelImageEditor = ({ channelId, image, updatedAt, name, size }: ChannelImageEditorProps) => {
  const [uploadImage, { loading: uploading }] = useMutation<
    UploadChannelImageMutation,
    UploadChannelImageMutationVariables
  >(UPLOAD_CHANNEL_IMAGE);
  const [deleteImage, { loading: deleting }] = useMutation<
    DeleteChannelImageMutation,
    DeleteChannelImageMutationVariables
  >(DELETE_CHANNEL_IMAGE);

  return (
    <EditableImage
      loading={uploading || deleting}
      hasImage={!!image}
      onUpload={async (photo) => {
        const result = await uploadImage({
          variables: { channelId, imageBase64: photo.base64, contentType: photo.contentType },
        });
        return result.data?.uploadChannelImage?.errors;
      }}
      onDelete={async () => {
        const result = await deleteImage({ variables: { channelId } });
        return result.data?.deleteChannelImage?.errors;
      }}
      modalTitle="Imagen del canal"
      deleteLabel="Eliminar imagen"
      editLabel="Cambiar imagen del canal"
      buttonTestId="edit-channel-image-button"
      loadingContent={
        <div
          className="flex items-center justify-center rounded-full bg-slate-100"
          style={{ width: size, height: size }}
        >
          <IonSpinner name="crescent" data-testid="channel-image-spinner" />
        </div>
      }
    >
      <ChannelImage image={image} updatedAt={updatedAt} name={name} size={size} />
    </EditableImage>
  );
};
